import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthRequestModel, UserModel } from '@tsin-core/models/auth.model';
import { environment } from '@tsin-env/environment';
import { Observable } from 'rxjs';

const AUTH_VALIDATE_USER = `${environment.authBaseUrl}/auth/authenticate-profiile`;
const AUTH_LOGOUT = `${environment.authBaseUrl}/auth/authenticate-profiile`;

const AUTH_VALIDATE_ADMIN = `${environment.wwuBaseUrl}/auth/authenticate-profiile`;
const AUTH_LOGOUT_ADMIN = `${environment.wwuBaseUrl}/auth/authenticate-profiile`;

const AUTH_VALIDATE_TOKEN = `${environment.apiUrl}/user/me`;

const AUTH_VALIDATE_TOKEN_WWU = `${environment.wwuBaseUrl}/user/me`;

/**
 * Endpoint on the shared Auth backend that exchanges a Cognito ID token for an
 * Exams-service JWT.  The Cognito token (set as an id_token cookie by auth-ui)
 * is validated against the Cognito User Pool; the Auth backend then issues a
 * short-lived JWT scoped to the EXAM service.
 *
 * Point de terminaison du backend Auth partagé qui échange un token Cognito ID
 * contre un JWT du service Examens.  Le token Cognito (mis en cookie id_token
 * par auth-ui) est validé; le backend Auth émet ensuite un JWT limité au service EXAM.
 */
const AUTH_COGNITO_EXCHANGE = `${environment.authBaseUrl}/auth/cognito/token-exchange`;

@Injectable({
  providedIn: 'root'
})

// The authentication service
export class AuthService {

  // Inject the http client into auth service
  http: HttpClient = inject(HttpClient);

  // Method for endpoint to validate the user and return a token
  validate(credentials: AuthRequestModel) {
    // console.log('CREDENTIALS', credentials)
    return this.http.post(AUTH_VALIDATE_USER, credentials)
  }

  // Method for endpoint to logout a user from the server
  logout() {
    return this.http.delete(AUTH_LOGOUT)
  }

  // Method for endpoint to validate the user and return a token
  validateAdmin(credentials: AuthRequestModel) {
    console.log('CREDENTIALS', credentials)
    return this.http.post(AUTH_VALIDATE_ADMIN, credentials)
  }

  validateToken(payload: { token: string; refreshToken: string }): Observable<UserModel> {
    return this.http.get<UserModel>(AUTH_VALIDATE_TOKEN, {
      headers: {
        "Authorization": `Bearer ${payload.token}`
      }
    })
  }

  validateTokenWWU(payload: { token: string; refreshToken: string }): Observable<UserModel> {
    return this.http.get<UserModel>(AUTH_VALIDATE_TOKEN_WWU, {
      headers: {
        "Authorization": `Bearer ${payload.token}`
      }
    })
  }

  refreshToken(payload: { refreshToken: string }): Observable<{ token: string; refreshToken: string }> {
    return this.http.post<{ token: string; refreshToken: string }>(`${environment.authBaseUrl}/auth/refresh`, payload);
  }

  /**
   * Exchange a Cognito ID token for an Exams-service JWT.
   *
   * Called by validate-token component when auth-ui has set the `id_token` cookie
   * after a successful Cognito login.  The Auth backend validates the Cognito token,
   * finds or creates the user, and returns a service-scoped JWT + user metadata.
   *
   * Échange un token Cognito ID contre un JWT du service Examens.
   * Appelé par le composant validate-token quand auth-ui a défini le cookie id_token.
   */
  exchangeCognitoToken(cognitoIdToken: string): Observable<{
    success: boolean;
    token: string;
    sessionId: string;
    expiresIn: number;
    user: { id: string; email: string; profiles: any[]; cognitoSub: string; services: string[]; roles: string[] };
  }> {
    return this.http.post<any>(AUTH_COGNITO_EXCHANGE, {
      cognitoIdToken,
      service: 'EXAM',
    });
  }

  // Method for endpoint to logout a user from the server
  logoutAdmin() {
    return this.http.delete(AUTH_LOGOUT_ADMIN)
  }
}
